import type { PickableEntityType } from '../../services/pickableEntities';

/**
 * Deep-link target catalog for the banner editor and the push composer. Mirrors
 * the app's supported navigation routes (watchlock `navigationRef.ts`). The
 * editors offer these as a dropdown; `:id` routes reveal either a picker or an
 * id field, "Custom URL" accepts a free `http(s)://…` marketing link, and
 * "None" clears the link.
 *
 * NOTE: this list is maintained from the task spec, not read from watchlock
 * directly (that repo isn't cloned here). If the app's route catalog changes,
 * update this file to match.
 */

/** A known in-app route. `idParam` set ⇒ the route needs an id segment. */
export interface DeepLinkTarget {
  /** Stable key used as the <select> option value + parse matching. */
  key: string;
  label: string;
  /** Template with `:id` where an id is required, else a static path. */
  template: string;
  /** Human label for the id field (e.g. "Watch ID"); absent ⇒ no id needed. */
  idParam?: string;
  /**
   * The picker tab that can fill `idParam` by name. Absent on an `:id` route
   * means the admin must still type the id — true of the legacy per-user
   * routes below, and of nothing we offer today.
   */
  pick?: PickableEntityType;
  /** Shown under the field when the route needs a newer app build. */
  requiresAppHint?: string;
  /** One-line explanation shown under the dropdown. */
  help?: string;
  /**
   * Append the picked entity's name as `?label=`. The app's calibre route
   * requires a `calibreName` param and seeds the screen title from `label`
   * (falling back to the bare id) — see watchlock `navigationRef.ts`. The label
   * is display-only there; the screen still resolves the calibre by id.
   */
  labelParam?: boolean;
  /**
   * An optional whole-number query parameter on a static route, e.g. What's
   * New's `?days=N`. Blank means "leave it off" (the app's own default);
   * filled, it must be an integer in [min, max].
   */
  optionalNumberParam?: {
    name: string;
    label: string;
    min: number;
    max: number;
    placeholder?: string;
  };
  /**
   * Shown only on the Share Links screen — for a route whose public https link
   * has a caveat the push composer and banner editor don't care about.
   */
  shareHint?: string;
}

/**
 * The two routes that reach a document belonging to ONE user.
 *
 * Removed from the composer dropdowns in the deep-link picker work: a banner or
 * a broadcast push is authored once and delivered to many people, so there is
 * no id the author could put here that is correct for more than a single
 * recipient. Offering the option implied otherwise, and the field's presence
 * was the whole of that implication.
 *
 * They stay in the PARSER — `parseDeepLink` scans them — so a banner saved
 * before the change still round-trips into its own option instead of
 * degrading to "Custom URL" and being rewritten as a bare path on the next
 * save. The app still routes both; nothing was removed from watchlock.
 *
 * If a per-user push ever ships, it wants a personalised `deepLink` resolved
 * per recipient at send time, not a constant typed into a composer.
 */
export const LEGACY_DEEP_LINK_TARGETS: DeepLinkTarget[] = [
  { key: 'watch', label: 'Watch (per-user)', template: '/watch/:id', idParam: 'Watch ID' },
  { key: 'event', label: 'Event (per-user)', template: '/event/:id', idParam: 'Event ID' },
];

/**
 * A newer route than the oldest builds in the field understand. Push reaches
 * every installed version, so these two are offered with the caveat visible
 * rather than withheld — an old client logs an unrecognised path and does
 * nothing, which is a dud tap, not a crash.
 */
const NEEDS_NEW_BUILD =
  'Needs a build with watchlock #993. Older installs will ignore the tap.';

export const DEEP_LINK_TARGETS: DeepLinkTarget[] = [
  {
    key: 'reference',
    label: 'Reference',
    template: '/reference/:id',
    idParam: 'Brand ID / Reference ID',
    pick: 'reference',
    help: 'A catalogue reference. Addressed by brand AND reference id — pick one to fill both.',
  },
  {
    key: 'brand',
    label: 'Brand',
    template: '/brand/:id',
    idParam: 'Brand ID',
    pick: 'brand',
  },
  {
    key: 'calibre',
    label: 'Movement (calibre)',
    template: '/calibre/:id',
    idParam: 'Calibre ID',
    pick: 'calibre',
    labelParam: true,
  },
  {
    key: 'manufacturer',
    label: 'Manufacturer',
    template: '/manufacturer/:id',
    idParam: 'Manufacturer ID',
    pick: 'manufacturer',
    requiresAppHint: NEEDS_NEW_BUILD,
  },
  {
    key: 'module',
    label: 'Electronic module',
    template: '/module/:id',
    idParam: 'Module ID',
    pick: 'module',
    requiresAppHint: NEEDS_NEW_BUILD,
  },
  {
    key: 'article',
    label: 'Article',
    template: '/article/:id',
    idParam: 'Article ID',
    pick: 'article',
  },
  {
    key: 'landing',
    label: 'Curated collection',
    template: '/landing/:id',
    idParam: 'Collection ID',
    pick: 'collection',
    help: 'Landing collections open their page; search collections redirect to Advanced Search with their filters.',
  },
  {
    /**
     * What's New (Confluence WK 2287861761). `?days=N` overrides the admin's
     * configured window for this one link — the app accepts 1-180 and falls
     * back to the configured window when it is absent.
     */
    key: 'whats-new',
    label: "What's New",
    template: '/whats-new',
    optionalNumberParam: {
      name: 'days',
      label: 'Days window (optional)',
      min: 1,
      max: 180,
      placeholder: "Blank = the configured What's New window",
    },
    help: "Opens the What's New page. Leave the days blank to use the window set in Configuration → What's New.",
    requiresAppHint: "Needs an app build with What's New. Older installs will ignore the tap.",
    shareHint:
      'On iOS the https link opens the app only once /whats-new is listed in the hosted ' +
      'apple-app-site-association (watchlock hosting/); until then use the watchkeeper:// link. ' +
      'Android opens either.',
  },
  {
    /**
     * The Curator free trial, as one pick rather than a URL to remember.
     *
     * `?plan=curator` is validated by the client against the plan union and
     * scrolls that tier's card into view, which is what makes this different
     * from a bare `/subscription`. The trial framing on the far side is the
     * app's own eligibility call: someone who has already used the trial lands
     * on the same card showing the plain upgrade price. The link is right for
     * both — only the copy differs.
     */
    key: 'curator-trial',
    label: '3 months free (Curator trial)',
    template: '/subscription?plan=curator',
    help: 'Opens the paywall scrolled to Curator. Shows the trial offer to eligible members, the upgrade price to everyone else.',
  },
  { key: 'notifications', label: 'Notifications', template: '/notifications' },
  {
    key: 'settings-notifications',
    label: 'Notification settings',
    template: '/settings/notifications',
  },
  {
    key: 'settings-data-management',
    label: 'Data management',
    template: '/settings/data-management',
  },
  { key: 'subscription', label: 'Subscription', template: '/subscription' },
];

/** Sentinel option keys the dropdown uses alongside the known targets. */
export const DEEP_LINK_NONE = 'none';
export const DEEP_LINK_CUSTOM = 'custom';

export type DeepLinkMode =
  | typeof DEEP_LINK_NONE
  | typeof DEEP_LINK_CUSTOM
  | string; // a DeepLinkTarget.key

export interface ParsedDeepLink {
  mode: DeepLinkMode;
  /** Populated for `:id` routes. */
  id: string;
  /** Populated for custom URLs. */
  customUrl: string;
  /**
   * The entity's name at the time it was picked, for the composer's chip. Not
   * part of the stored link — re-parsing an existing record leaves it empty and
   * the chip falls back to showing the id.
   */
  label?: string;
  /** The value of the target's `optionalNumberParam`, as typed ('' or absent = leave it off). */
  param?: string;
}

/** Look up a known target by its key, including the parse-only legacy ones. */
export function findTarget(key: string): DeepLinkTarget | undefined {
  return (
    DEEP_LINK_TARGETS.find((t) => t.key === key) ??
    LEGACY_DEEP_LINK_TARGETS.find((t) => t.key === key)
  );
}

/** True when the string is an external `http(s)://…` URL. */
export function isHttpUrl(value: string): boolean {
  return /^https?:\/\/\S+$/i.test(value.trim());
}

/**
 * Parse a stored `deepLink` string back into editor state so an existing banner
 * round-trips into the dropdown. Matches static routes exactly and `:id` routes
 * by prefix; anything else (and any `http(s)://` URL) falls to Custom.
 *
 * Static targets are matched BEFORE `:id` ones. `/subscription?plan=curator`
 * and `/subscription` are both static, and the longest exact match has to win
 * or the trial link would parse back as a plain subscription link and lose its
 * plan on the next save.
 */
export function parseDeepLink(deepLink: string | null | undefined): ParsedDeepLink {
  const value = (deepLink ?? '').trim();
  if (!value) return { mode: DEEP_LINK_NONE, id: '', customUrl: '' };

  const all = [...DEEP_LINK_TARGETS, ...LEGACY_DEEP_LINK_TARGETS];

  for (const target of all) {
    if (!target.idParam && value === target.template) {
      return { mode: target.key, id: '', customUrl: '' };
    }
  }

  // A static route with its optional number param, e.g. `/whats-new?days=30`.
  // Only that one param is recognised; anything else stays Custom so it is
  // never silently rewritten.
  for (const target of all) {
    const p = target.optionalNumberParam;
    if (!p || target.idParam) continue;
    const m = value.match(new RegExp(`^${escapeForRegExp(target.template)}\\?${p.name}=(\\d+)$`));
    if (m) return { mode: target.key, id: '', customUrl: '', param: m[1] };
  }

  for (const target of all) {
    if (!target.idParam) continue;
    // e.g. template "/brand/:id" ⇒ prefix "/brand/"
    const prefix = target.template.replace(':id', '');
    if (value.startsWith(prefix) && value.length > prefix.length) {
      const rest = value.slice(prefix.length);
      // A labelled route carries `?label=` after the id. Split it back off, or
      // the next save would write it into the id and append a second label.
      if (target.labelParam) {
        const q = rest.indexOf('?');
        if (q !== -1) {
          const label = new URLSearchParams(rest.slice(q + 1)).get('label')?.trim();
          return {
            mode: target.key,
            id: rest.slice(0, q),
            customUrl: '',
            ...(label ? { label } : {}),
          };
        }
      }
      return {
        mode: target.key,
        id: rest,
        customUrl: '',
      };
    }
  }

  return { mode: DEEP_LINK_CUSTOM, id: '', customUrl: value };
}

/**
 * Compose the editor state back into a stored `deepLink` string (or `null` for
 * None / incomplete input). Returns `{ value, error }` — `error` is set when the
 * chosen mode has missing/invalid input so the editor can block Save.
 */
export function buildDeepLink(parsed: ParsedDeepLink): {
  value: string | null;
  error: string | null;
} {
  if (parsed.mode === DEEP_LINK_NONE) return { value: null, error: null };

  if (parsed.mode === DEEP_LINK_CUSTOM) {
    const url = parsed.customUrl.trim();
    if (!url) return { value: null, error: 'Enter a custom URL' };
    if (!isHttpUrl(url)) {
      return { value: null, error: 'Custom URL must start with http:// or https://' };
    }
    return { value: url, error: null };
  }

  const target = findTarget(parsed.mode);
  if (!target) return { value: null, error: 'Unknown deep-link target' };

  if (target.idParam) {
    const id = parsed.id.trim();
    if (!id) return { value: null, error: `${target.idParam} is required` };
    /**
     * A reference is addressed by brand AND reference id. The picker always
     * produces both, but the field stays typeable, and the app refuses a
     * one-segment `/reference/rolex` rather than guessing — so catch it here,
     * where the author can still see why, instead of at a dead tap.
     */
    if (target.key === 'reference' && id.split('/').filter(Boolean).length !== 2) {
      return {
        value: null,
        error: 'A reference needs both ids, as brandId/referenceId',
      };
    }
    const path = target.template.replace(':id', id);
    const label = target.labelParam ? parsed.label?.trim() : undefined;
    return {
      value: label ? `${path}?label=${encodeURIComponent(label)}` : path,
      error: null,
    };
  }

  const p = target.optionalNumberParam;
  const raw = parsed.param?.trim() ?? '';
  if (p && raw) {
    const n = Number(raw);
    if (!/^\d+$/.test(raw) || !Number.isInteger(n) || n < p.min || n > p.max) {
      return { value: null, error: `${p.label.replace(/ \(optional\)$/, '')} must be a whole number from ${p.min} to ${p.max}` };
    }
    return { value: `${target.template}?${p.name}=${n}`, error: null };
  }

  return { value: target.template, error: null };
}

function escapeForRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
